import React, { useState, useEffect, useMemo } from 'react';
import { Box, Container, Typography } from '@mui/material';
import {
  SearchAndFilter,
  CategoryFilter,
  GalleryStats,
  LoadingGrid,
  ProductCard,
  ProductModal,
  FloatingWhatsApp,
  GalleryCTA,
  galleryItems,
  GalleryItem,
  ViewMode,
  SortOption
} from '../Gallery';
import { SEOHead, LocalBusinessSchema, BreadcrumbSchema, ProductSchema } from '../SEO';

const GalleryPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortOption>('popularity');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedProduct, setSelectedProduct] = useState<GalleryItem | null>(null);
  const [favorites, setFavorites] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Simulate loading effect
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [selectedCategory]);

  // Get unique categories for filter
  const categories = ['All', ...Array.from(new Set(galleryItems.map(item => item.category)))];
  
  // Enhanced filtering and sorting logic
  const filteredAndSortedItems = useMemo(() => {
    let filtered = galleryItems;

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(item =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.features.some(feature => feature.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Sort items
    switch (sortBy) {
      case 'popularity':
        return filtered.sort((a, b) => b.reviews - a.reviews);
      case 'rating':
        return filtered.sort((a, b) => b.rating - a.rating);
      case 'newest':
        return filtered.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
      default:
        return filtered;
    }
  }, [selectedCategory, searchQuery, sortBy]);

  // Toggle favorite
  const toggleFavorite = (productId: number) => {
    setFavorites(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  // Handle product detail view
  const handleProductClick = (product: GalleryItem) => {
    setSelectedProduct(product);
  };

  const breadcrumbs = BreadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Gallery", url: "/gallery" }
  ]);

  const featuredProducts = galleryItems.slice(0, 6).map(item => 
    ProductSchema({
      name: item.title,
      description: item.description,
      category: item.category,
      availability: item.availability
    })
  );

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [LocalBusinessSchema, breadcrumbs, ...featuredProducts]
  };

  return (
    <>
      <SEOHead
        title="Tiles Gallery - Premium Collection | Maa Saryu Agency Ayodhya | View 1000+ Designs"
        description="Explore our premium tiles gallery with 1000+ designs. Ceramic, porcelain, marble tiles, granite, sanitary ware & bathroom fittings. View prices & get instant quotes in Ayodhya."
        keywords={[
          "tiles gallery ayodhya",
          "tiles collection ayodhya", 
          "bathroom tiles gallery",
          "floor tiles gallery ayodhya",
          "ceramic tiles collection",
          "porcelain tiles gallery",
          "marble tiles gallery ayodhya",
          "granite gallery ayodhya",
          "sanitary ware gallery",
          "bathroom fittings gallery",
          "kitchen tiles gallery",
          "wall tiles collection",
          "tiles showroom gallery ayodhya",
          "tiles designs ayodhya",
          "modern tiles collection",
          "premium tiles gallery"
        ]}
        canonicalUrl="/gallery"
        ogTitle="Premium Tiles Gallery - 1000+ Designs | Maa Saryu Agency Ayodhya"
        ogDescription="🏠 Explore our premium tiles gallery with 1000+ designs. Ceramic, porcelain, marble tiles & more. Get instant quotes & expert consultation in Ayodhya!"
        structuredData={structuredData}
      />
      <Box sx={{ py: 8, backgroundColor: '#f8f9fa', minHeight: '80vh' }}>
      <Container maxWidth="lg">
        {/* Enhanced Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h1" sx={{ 
            fontWeight: 'bold', 
            color: '#1a237e',
            mb: 2,
            fontSize: { xs: '2rem', md: '3rem' },
            background: 'linear-gradient(45deg, #1a237e, #3949ab)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Product Gallery
          </Typography>
          <Typography variant="h6" sx={{ 
            color: '#666',
            maxWidth: '700px',
            mx: 'auto',
            lineHeight: 1.6,
            mb: 1
          }}>
            Discover our premium collection of tiles, granite, bathroom fittings, sanitary ware, and home decor products
          </Typography>
          <Typography variant="body1" sx={{ 
            color: '#888',
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: 1.5
          }}>
            Quality materials for your dream home - from construction to decoration
          </Typography>
        </Box>

        {/* Search and Filter Component */}
        <SearchAndFilter
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          sortBy={sortBy}
          onSortChange={setSortBy}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          favoritesCount={favorites.length}
        />

        {/* Category Filter Component */}
        <CategoryFilter
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
        />

        {/* Gallery Stats Component */}
        <GalleryStats
          filteredItemsCount={filteredAndSortedItems.length}
          selectedCategory={selectedCategory}
          searchQuery={searchQuery}
          allItems={galleryItems}
        />

        {/* Products Grid/List */}
        {isLoading ? (
          <LoadingGrid />
        ) : (
          <Box sx={{ 
            display: viewMode === 'grid' ? 'grid' : 'flex',
            flexDirection: viewMode === 'list' ? 'column' : undefined,
            gridTemplateColumns: viewMode === 'grid' ? { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(3, 1fr)' 
            } : undefined, 
            gap: 4 
          }}>
            {filteredAndSortedItems.map((item) => (
              <ProductCard
                key={item.id}
                item={item}
                viewMode={viewMode}
                isFavorite={favorites.includes(item.id)}
                onToggleFavorite={toggleFavorite}
                onProductClick={handleProductClick}
              />
            ))}
          </Box>
        )}

        {/* CTA Section */}
        <GalleryCTA />

        {/* Product Modal */}
        <ProductModal
          product={selectedProduct}
          open={!!selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />

        {/* Floating WhatsApp Button */}
        <FloatingWhatsApp />
        </Container>
      </Box>
    </>
  );
};

export default GalleryPage;